/**
 * Print row behaviour that CSS cannot do:
 *
 * 1. Marks each print that is sold out in the selected size. The reader changes the size in the
 *    browser and the page does not render again, so the server cannot mark them.
 * 2. Moves sold-out prints after in-stock ones.
 * 3. Remembers whether the reader expanded the rows.
 */

(function () {
  if (customElements.get('print-swatch-rows')) return;

  /* Remembers expanded or collapsed across print switches.

     A print click does not navigate. component-product.js renders the section again over AJAX.
     That removes this element and builds a new one, so state on the element is lost. Opening a
     print page directly is a real navigation. sessionStorage covers both cases. It also keeps
     the setting in this tab only. */
  var STATE_KEY = 'svaha:print-rows-expanded';

  function readExpanded() {
    try {
      return sessionStorage.getItem(STATE_KEY) === '1';
    } catch (error) {
      /* Private mode and blocked site data throw here. */
      return false;
    }
  }

  /* The size in use at the last render. This stops a flash after a print switch.

     New markup arrives with no size radio checked, because component-product.js restores the
     size afterwards, and it waits in a poll loop to do so. The first render therefore saw no
     size, drew every print unmarked in date order, then reordered once the restore fired a
     change event.

     This variable makes the first render correct, so the later restore changes nothing.

     It is a module variable, not sessionStorage. It must survive an AJAX swap, where the theme
     restores the size. It must not survive a new page load, where no size is checked and marked
     prints would contradict an empty size selector. */
  var lastKnownSize = null;

  function writeExpanded(expanded) {
    try {
      sessionStorage.setItem(STATE_KEY, expanded ? '1' : '0');
    } catch (error) {
      /* Not fatal. The rows go back to collapsed at the next render. */
    }
  }

  customElements.define(
    'print-swatch-rows',
    class extends HTMLElement {
      connectedCallback() {
        this.run = this.querySelector('.variant-picker__option-values');
        if (!this.run) return;

        this.items = Array.from(this.run.querySelectorAll('.thumbnail-swatch'));
        if (!this.items.length) return;

        this.toggle = document.createElement('button');
        this.toggle.type = 'button';
        this.toggle.className = 'print-swatch-rows__more';
        this.toggle.addEventListener('click', () => {
          this.classList.toggle('is-expanded');
          writeExpanded(this.classList.contains('is-expanded'));
          /* Render again, not just relabel. Collapse pins the selected print. Expand puts it
             back in date order. */
          this.render();
        });
        this.run.appendChild(this.toggle);

        /* Restore before the first render, so the pin sees the right state. */
        if (readExpanded()) this.classList.add('is-expanded');

        this.render();

        this.sizeScope = this.closest('.product-info') || document;
        this.onSizeChange = () => this.render();
        this.sizeScope.addEventListener('change', this.onSizeChange);

        /* The hidden count comes from the column count. CSS sets that with a container query,
           so it changes on resize and nothing tells the script. This observer only relabels the
           toggle. */
        if ('ResizeObserver' in window) {
          this.lastWidth = 0;
          this.observer = new ResizeObserver((entries) => {
            const width = Math.round(entries[0].contentRect.width);
            if (width === this.lastWidth) return;
            this.lastWidth = width;
            this.syncToggle();
          });
          this.observer.observe(this);
        }
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
        if (this.sizeScope) {
          this.sizeScope.removeEventListener('change', this.onSizeChange);
        }
      }

      /* Every size that any print can be bought in. This finds the size radio group without a
         fixed option position. The position changes between products. */
      sizeVocabulary() {
        const all = new Set();
        this.items.forEach((item) => {
          (item.dataset.availSizes || '').split(',').forEach((size) => {
            const trimmed = size.trim();
            if (trimmed) all.add(trimmed);
          });
        });
        return all;
      }

      /* Returns null until the reader picks a size. option-value.liquid leaves the size radios
         unchecked on load, so no size is the normal first state. */
      selectedSize() {
        const vocabulary = this.sizeVocabulary();
        if (!vocabulary.size) return null;

        const scope = this.sizeScope || document;
        const checked = scope.querySelectorAll('input[type="radio"]:checked');

        for (const input of checked) {
          if (vocabulary.has(input.value)) return input.value;
        }

        if (lastKnownSize !== null && vocabulary.has(lastKnownSize)) {
          return lastKnownSize;
        }

        return null;
      }

      /* CSS owns the cap, in a container query, and publishes it as --print-rows-cap. Reading it
         back beats counting hidden prints: the count only works while the rows are collapsed, and
         the expanded state is remembered between prints. */
      cap() {
        const raw = getComputedStyle(this.run).getPropertyValue('--print-rows-cap');
        const value = parseInt(raw, 10);
        return Number.isFinite(value) ? value : this.items.length;
      }

      syncToggle() {
        const expanded = this.classList.contains('is-expanded');
        const overflow = this.items.length - this.cap();

        /* A group can hold fewer prints than the rows fit. Then there is nothing to expand, so the
           button stays away whatever the remembered state says. */
        if (overflow <= 0) {
          this.toggle.hidden = true;
          return;
        }

        this.toggle.hidden = false;
        this.toggle.textContent = expanded ? '−' : '+' + overflow;
        this.toggle.title = expanded ? 'Show only the first three rows' : 'Show ' + overflow + ' more prints';
        this.toggle.setAttribute('aria-label', this.toggle.title);
        this.toggle.setAttribute('aria-expanded', String(expanded));
      }

      render() {
        const size = this.selectedSize();
        if (size !== null) lastKnownSize = size;

        /* Split in document order. This keeps the server order inside each half. */
        const inStock = [];
        const soldOut = [];

        this.items.forEach((item) => {
          const sizes = (item.dataset.availSizes || '').split(',').map((value) => value.trim());
          const unavailable = size !== null && !sizes.includes(size);

          if (item.classList.contains('is-disabled') !== unavailable) {
            item.classList.toggle('is-disabled', unavailable);
          }

          const image = item.querySelector('img');
          const name = item.dataset.printName || (image ? image.alt : '') || item.title;
          item.dataset.printName = name;

          const text = unavailable ? name + ' — sold out in ' + size : name;
          if (image && image.alt !== text) image.alt = text;
          if (item.title !== text) item.title = text;

          (unavailable ? soldOut : inStock).push(item);
        });

        let ordered = inStock.concat(soldOut);

        /* When the rows are collapsed, move the selected print to the slot before the toggle,
           but only if the cap would hide it. Without this, an older print's page shows no
           selected print at all.

           Expand puts the print back in date order, because the pin only applies when collapsed.
           This works the same for in-stock and sold-out prints. The pin runs after the split, so
           a sold-out print keeps its fade and sits in the last slot. */
        if (!this.classList.contains('is-expanded')) {
          const cap = this.cap();
          const selectedIndex = ordered.findIndex((item) => item.classList.contains('is-selected'));

          if (cap > 0 && cap < ordered.length && selectedIndex >= cap) {
            const selected = ordered[selectedIndex];
            const rest = ordered.filter((item) => item !== selected);
            ordered = rest
              .slice(0, cap - 1)
              .concat([selected])
              .concat(rest.slice(cap - 1));
          }
        }

        const unchanged = ordered.every((item, i) => this.run.children[i] === item);
        if (!unchanged) {
          ordered.forEach((item) => this.run.appendChild(item));
        }

        /* Keep the toggle as the last child. CSS hides capped prints with display:none, so the
           toggle moves into the last slot of row three. */
        this.run.appendChild(this.toggle);

        this.syncToggle();
      }
    },
  );
})();
